import { useFormContext } from 'react-hook-form';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getFeesAndTaxesState } from 'store/selectors/controls.selector';
import VehicleApi from 'api/vehicleApi';
import { getCurrentVendorMappings } from 'store/selectors/ui.selector';
import { LENDERS_MOTHLY_INCOME_DEFAULT_VALUES } from 'modules/RACAffordabilityEngine/RacMontlyIncomeModal/consts/defaultValues';
import { getUser } from 'store/selectors/auth.selector';
import { resetDealSourceData, resetDealSourceDataFlags } from 'store/reducers/lenders.reducer';
import { SHOP_BY_CALLBACK_INITIAL_VALUES } from 'modules/Lender/Lender.const';
import { getRouteOneDealers } from 'store/selectors/dealer.selector';
import { resetCreatedUserVehicle } from 'store/reducers/vehicle.reducer';

import { IUser, IVehicleForm } from '../types';

interface IUseVehicleFormReset {
  resetInputs: VoidFunction;
  resetFilters: VoidFunction;
  resetAll: VoidFunction;
}

export const useVehicleFormReset = (
  initialValues: IVehicleForm,
  resetStateCallback?: VoidFunction,
  resetVehicleFiltersDirtyFields?: VoidFunction,
  ltvTaxesFeesFilter?: string,
  isLender?: boolean,
  isResetDealSourceFields?: boolean,
  defaultVendorFieldInternalValue?: string,
): IUseVehicleFormReset => {
  const dispatch = useDispatch();
  const { reset, getValues, setValue, clearErrors } = useFormContext();
  const { estimatedFeesControl, salesTaxRateControl, insuranceControl } = useSelector(getFeesAndTaxesState);
  const vendorOptions = useSelector(getCurrentVendorMappings);
  const user = useSelector(getUser) as IUser;
  const dealersList = useSelector(getRouteOneDealers);

  const { rateSheetSettingsModal, feesAndTaxes, routeOneDealer } = getValues();

  const currentDealer = useMemo(
    () => (routeOneDealer ? dealersList.find((dealer) => dealer.route_one_id === routeOneDealer.value) : undefined),
    [dealersList, routeOneDealer],
  );

  const feesAndTaxesInitialValues = useMemo(
    () => ({
      ...initialValues.feesAndTaxes,
      salesTaxRate: currentDealer ? currentDealer.sales_tax_rate : salesTaxRateControl,
      estimatedFees: estimatedFeesControl,
      salesFees: estimatedFeesControl,
      vscAndGapInsurance: insuranceControl,
    }),
    [initialValues.feesAndTaxes, salesTaxRateControl, estimatedFeesControl, insuranceControl, currentDealer],
  );

  const withoutInventoryResetInitialValues = useCallback(
    () => ({
      feesAndTaxes: {
        ...feesAndTaxesInitialValues,
        salesTaxRate: feesAndTaxes.salesTaxRate,
      },
      rateSheetSettingsModal: rateSheetSettingsModal,
    }),
    [feesAndTaxes.salesTaxRate, feesAndTaxesInitialValues, rateSheetSettingsModal],
  );
  const withoutInventoryInitialValues = useCallback(
    () => (user?.isWithInventory ? {} : withoutInventoryResetInitialValues()),
    [user?.isWithInventory, withoutInventoryResetInitialValues],
  );

  const preferences = useMemo(
    () => ({
      ...initialValues.preferences,
      ltvTaxesFeesFilter: ltvTaxesFeesFilter || initialValues.preferences.ltvTaxesFeesFilter,
      vendorField:
        initialValues.preferences.vendorField || defaultVendorFieldInternalValue || Object.keys(vendorOptions)[0],
    }),
    [initialValues.preferences, ltvTaxesFeesFilter, vendorOptions, defaultVendorFieldInternalValue],
  );

  const resetState = useCallback(() => {
    resetStateCallback?.();
  }, [resetStateCallback]);

  const resetDealSourceFields = useCallback(() => {
    if (isResetDealSourceFields) {
      const routeOneSearchValue = user?.is_sso ? user?.originalConversationId ?? '' : '';
      const routeOneDealerId = user?.is_sso && !user?.isWithInventory ? user?.originalExternalDealerId ?? '' : '';
      let routeOneDealer = undefined;

      if (user?.is_sso && user?.isWithInventory) {
        const dealer = dealersList.find((dealer) => dealer.route_one_id === user?.originalExternalDealerId);

        if (dealer) {
          routeOneDealer = { value: dealer.route_one_id, title: dealer.name };
        }
      }

      setValue('routeOneSearch', routeOneSearchValue);
      setValue('routeOneDealer', routeOneDealer);
      setValue('routeOneDealerId', routeOneDealerId);
    }
  }, [
    isResetDealSourceFields,
    setValue,
    user?.is_sso,
    user?.originalConversationId,
    user?.originalExternalDealerId,
    user?.isWithInventory,
    dealersList,
  ]);

  const clearDealSourceState = useCallback(() => {
    if (!isResetDealSourceFields) return;

    if (isLender) {
      dispatch(resetDealSourceData());
      dispatch(resetDealSourceDataFlags());
    }
  }, [dispatch, isLender, isResetDealSourceFields]);

  const resetInputs = useCallback(() => {
    const { vehicleFilters, ...otherInitialValues } = initialValues;
    const { tableView, stocks, ...otherFormValues } = getValues();

    VehicleApi.cancelAllRequests();

    reset({
      ...otherFormValues,
      ...otherInitialValues,
      tableView,
      stocks,
      feesAndTaxes: feesAndTaxesInitialValues,
      lendersMonthlyIncomeModal: LENDERS_MOTHLY_INCOME_DEFAULT_VALUES,
      preferences: preferences,
      netDownPayment: undefined,
      frontAndBackEndsAdds: isLender ? undefined : insuranceControl,
      totalFrontAndBackEndsAddsDialog: {
        addOns: undefined,
        gap: undefined,
        vsc: isLender ? undefined : insuranceControl,
      },
      shopByCallback: SHOP_BY_CALLBACK_INITIAL_VALUES,
      ...withoutInventoryInitialValues(),
    });

    resetState();
    resetDealSourceFields();
    clearDealSourceState();

    setTimeout(() => clearErrors());
  }, [
    initialValues,
    getValues,
    reset,
    clearErrors,
    feesAndTaxesInitialValues,
    preferences,
    insuranceControl,
    isLender,
    withoutInventoryInitialValues,
    resetState,
    resetDealSourceFields,
    clearDealSourceState,
  ]);

  const resetFilters = useCallback(() => {
    reset(
      {
        ...getValues(),
        vehicleFilters: initialValues.vehicleFilters,
        accordionShown: initialValues.vehicleTableAccordion,
        tableView: initialValues.tableView,
        stocks: initialValues.stocks,
      },
      { isValid: true },
    );

    if (resetVehicleFiltersDirtyFields) resetVehicleFiltersDirtyFields();
  }, [
    reset,
    getValues,
    initialValues.vehicleFilters,
    initialValues.vehicleTableAccordion,
    initialValues.tableView,
    initialValues.stocks,
    resetVehicleFiltersDirtyFields,
  ]);

  const resetAll = useCallback(() => {
    const { tableView, ...otherFormValues } = getValues();

    VehicleApi.cancelAllRequests();

    reset({
      ...otherFormValues,
      ...initialValues,
      tableView: initialValues.tableView,
      selectedColumns: undefined,
      feesAndTaxes: feesAndTaxesInitialValues,
      lendersMonthlyIncomeModal: LENDERS_MOTHLY_INCOME_DEFAULT_VALUES,
      preferences: preferences,
      netDownPayment: undefined,
      frontAndBackEndsAdds: isLender ? undefined : insuranceControl,
      totalFrontAndBackEndsAddsDialog: {
        addOns: undefined,
        gap: undefined,
        vsc: isLender ? undefined : insuranceControl,
      },
      shopByCallback: SHOP_BY_CALLBACK_INITIAL_VALUES,
      ...withoutInventoryInitialValues(),
    });

    if (resetVehicleFiltersDirtyFields) resetVehicleFiltersDirtyFields();

    resetState();
    resetDealSourceFields();
    clearDealSourceState();
    dispatch(resetCreatedUserVehicle());

    setTimeout(() => clearErrors());
  }, [
    getValues,
    reset,
    initialValues,
    isLender,
    feesAndTaxesInitialValues,
    preferences,
    insuranceControl,
    withoutInventoryInitialValues,
    resetVehicleFiltersDirtyFields,
    resetState,
    resetDealSourceFields,
    clearDealSourceState,
    clearErrors,
    dispatch,
  ]);

  return {
    resetInputs,
    resetFilters,
    resetAll,
  };
};
